import type { CentreApprovalRequest } from '@ielts-buddy/shared'
import { useCallback, useEffect, useState } from 'react'
import {
  approveCentreRequest,
  fetchPendingCentreRequests,
  rejectCentreRequest,
} from '../shared/api.js'
import { AppNav } from '../shared/ui/AppNav.js'
import { Button } from '../shared/ui/Button.js'
import { Card } from '../shared/ui/Card.js'
import { ErrorState } from '../shared/ui/ErrorState.js'
import { LoadingDots } from '../shared/ui/LoadingDots.js'

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export function CentresManagementScreen() {
  const [requests, setRequests] = useState<CentreApprovalRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busyCentreId, setBusyCentreId] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { requests: queue } = await fetchPendingCentreRequests()
      setRequests(queue)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const review = async (
    centreId: string,
    action: 'approve' | 'reject',
  ): Promise<void> => {
    setBusyCentreId(centreId)
    setError(null)
    try {
      if (action === 'approve') {
        await approveCentreRequest(centreId)
      } else {
        await rejectCentreRequest(centreId)
      }
      setRequests((prev) => prev.filter((r) => r.centreId !== centreId))
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusyCentreId(null)
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <AppNav breadcrumb="Centres Management" />

      <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-6 sm:py-8 animate-fade-in">
        {loading ? (
          <div className="flex justify-center py-20">
            <LoadingDots label="Loading approval queue…" />
          </div>
        ) : error ? (
          <ErrorState
            title="Failed to load approval queue"
            message={error}
            primaryAction={{ label: 'Retry', onClick: load }}
          />
        ) : (
          <Card tone="surface" className="overflow-hidden">
            <div className="px-5 py-4 border-b border-brand-border flex items-center justify-between">
              <h2 className="font-semibold text-brand-text text-sm">
                Pending Registrations
              </h2>
              <span className="text-xs text-brand-text-subtle bg-brand-bg-soft border border-brand-border rounded-full px-2 py-0.5">
                {requests.length}
              </span>
            </div>

            {requests.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-14 text-center">
                <div className="w-10 h-10 rounded-full bg-brand-bg-soft border border-brand-border flex items-center justify-center text-brand-text-subtle text-lg">
                  ✅
                </div>
                <p className="text-brand-text-muted text-sm font-medium">
                  No pending requests
                </p>
                <p className="text-brand-text-subtle text-xs">
                  New centre registrations will appear here
                </p>
              </div>
            ) : (
              <div className="divide-y divide-brand-border">
                {requests.map((request) => {
                  const isBusy = busyCentreId === request.centreId
                  return (
                    <div
                      key={request.centreId}
                      className="px-5 py-4 flex flex-col md:flex-row md:items-center gap-3 md:justify-between"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-brand-text truncate">
                          {request.centreName}
                        </p>
                        <p className="text-xs text-brand-text-muted">
                          Owner: {request.ownerFirstName}
                          {request.ownerLastName
                            ? ` ${request.ownerLastName}`
                            : ''}
                          {request.ownerUsername
                            ? ` (@${request.ownerUsername})`
                            : ''}
                        </p>
                        <p className="text-xs text-brand-text-subtle">
                          Requested: {formatDate(request.requestedAt)} ·
                          Telegram ID: {request.ownerTelegramId}
                        </p>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={isBusy}
                          onClick={() =>
                            void review(request.centreId, 'reject')
                          }
                        >
                          Reject
                        </Button>
                        <Button
                          size="sm"
                          disabled={isBusy}
                          onClick={() =>
                            void review(request.centreId, 'approve')
                          }
                        >
                          Approve
                        </Button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </Card>
        )}
      </main>
    </div>
  )
}
